// src/components/acciones/CurvedText.js
import React, { memo, useId, useMemo } from 'react';

// Valor intermedio `CurvedText` utilizado por la lógica de este bloque.
const CurvedText = memo(function CurvedText({
  text = '',
  width = 300,
  height = 50,
  curve = 'up',
  fontSize = 14,
  color = '#2d4a3e',
  fontFamily = "'Dollar Bill', serif",
  curveIntensity = 25,
  letterSpacing = 2,
  maxCharsPerLine = 18,
  lineSpacing = -0.15,
  showOutline = true,
  outlineWidth = 0.5,
  outlineColor = '#ffffff',
  showShadow = true,
  shadowOffsetX = 2,
  shadowOffsetY = 2,
  shadowOpacity = 0.55,
  className = '',
  style = {}
}) {
  // Resultado `uniqueId` obtenido mediante el hook `useId`.
  const uniqueId = useId();

  // ============================================
  // DIVIDIR TEXTO EN LÍNEAS
  // ============================================
  const textLines = useMemo(() => {
    if (text.length <= maxCharsPerLine) {
      return [text];
    }
    
    // Valor intermedio `words` utilizado por la lógica de este bloque.
    const words = text.split(' ');
    
    if (words.length === 1) {
      return [text];
    }
    
    // Valor intermedio `bestSplit` utilizado por la lógica de este bloque.
    let bestSplit = -1;
    // Valor intermedio `bestDiff` utilizado por la lógica de este bloque.
    let bestDiff = Infinity;
    
    for (let i = 1; i < words.length; i++) {
      // Valor intermedio `line1` utilizado por la lógica de este bloque.
      const line1 = words.slice(0, i).join(' ');
      // Valor intermedio `line2` utilizado por la lógica de este bloque.
      const line2 = words.slice(i).join(' ');
      
      // Valor intermedio `diff` utilizado por la lógica de este bloque.
      const diff = Math.abs(line1.length - line2.length);
      // Valor intermedio `line1Longer` utilizado por la lógica de este bloque.
      const line1Longer = line1.length >= line2.length;
      // Valor intermedio `adjustedDiff` utilizado por la lógica de este bloque.
      const adjustedDiff = line1Longer ? diff : diff + 5;
      
      if (adjustedDiff < bestDiff) {
        bestDiff = adjustedDiff;
        bestSplit = i;
      }
    }
    
    if (bestSplit === -1) {
      return [text];
    }
    
    // Valor intermedio `line1` utilizado por la lógica de este bloque.
    const line1 = words.slice(0, bestSplit).join(' ');
    // Valor intermedio `line2` utilizado por la lógica de este bloque.
    const line2 = words.slice(bestSplit).join(' ');
    
    return [line1, line2];
  }, [text, maxCharsPerLine]);

  // Indicador booleano `isTwoLines` que representa la condición señalada por su nombre.
  const isTwoLines = textLines.length > 1;

  // ============================================
  // CALCULAR TAMAÑO DE FUENTE DINÁMICO
  // ============================================
  const getDynamicFontSize = (lineText, lineWidth, baseFontSize, ls = letterSpacing) => {
    // Valor intermedio `textLength` utilizado por la lógica de este bloque.
    const textLength = lineText.length;
    // Valor intermedio `maxWidth` utilizado por la lógica de este bloque.
    const maxWidth = lineWidth * 0.85;
    // Valor intermedio `charWidth` utilizado por la lógica de este bloque.
    const charWidth = baseFontSize * 0.62 + ls;
    // Valor intermedio `estimatedWidth` utilizado por la lógica de este bloque.
    const estimatedWidth = textLength * charWidth;
    
    if (estimatedWidth > maxWidth) {
      // Valor intermedio `scale` utilizado por la lógica de este bloque.
      const scale = maxWidth / estimatedWidth;
      return Math.max(baseFontSize * scale, 3);
    }
    return baseFontSize;
  };

  // ============================================
  // GENERAR PATH CURVO SUAVE
  // Dos curvas cúbicas Bézier unidas en el centro
  // Extremos planos → montañita central → planos
  // ============================================
  const getPath = (lineWidth, lineHeight, curveType, intensity, yOffset = 0) => {
    // Valor intermedio `curveAmount` utilizado por la lógica de este bloque.
    const curveAmount = (intensity / 100) * lineHeight * 0.8;
    
    switch (curveType) {
      case 'up': {
        // Valor intermedio `baseY` utilizado por la lógica de este bloque.
        const baseY = lineHeight * 0.85 + yOffset;
        // Valor intermedio `peakY` utilizado por la lógica de este bloque.
        const peakY = baseY - curveAmount;
        // Valor intermedio `midX` utilizado por la lógica de este bloque.
        const midX = lineWidth / 2;
        
        // Valor intermedio `cp1x` utilizado por la lógica de este bloque.
        const cp1x = lineWidth * 0.25;
        // Valor intermedio `cp1y` utilizado por la lógica de este bloque.
        const cp1y = baseY;
        // Valor intermedio `cp2x` utilizado por la lógica de este bloque.
        const cp2x = lineWidth * 0.35;
        // Valor intermedio `cp2y` utilizado por la lógica de este bloque.
        const cp2y = peakY;
        
        // Valor intermedio `cp3x` utilizado por la lógica de este bloque.
        const cp3x = lineWidth * 0.65;
        // Valor intermedio `cp3y` utilizado por la lógica de este bloque.
        const cp3y = peakY;
        // Valor intermedio `cp4x` utilizado por la lógica de este bloque.
        const cp4x = lineWidth * 0.75;
        // Valor intermedio `cp4y` utilizado por la lógica de este bloque.
        const cp4y = baseY;
        
        return `M 0,${baseY} C ${cp1x},${cp1y} ${cp2x},${cp2y} ${midX},${peakY} C ${cp3x},${cp3y} ${cp4x},${cp4y} ${lineWidth},${baseY}`;
      }
      
      case 'down': {
        // Valor intermedio `baseY` utilizado por la lógica de este bloque.
        const baseY = lineHeight * 0.25 + yOffset;
        // Valor intermedio `valleyY` utilizado por la lógica de este bloque.
        const valleyY = baseY + curveAmount * 0.5;
        // Valor intermedio `midX` utilizado por la lógica de este bloque.
        const midX = lineWidth / 2;
        
        // Valor intermedio `cp1x` utilizado por la lógica de este bloque.
        const cp1x = lineWidth * 0.25;
        // Valor intermedio `cp1y` utilizado por la lógica de este bloque.
        const cp1y = baseY;
        // Valor intermedio `cp2x` utilizado por la lógica de este bloque.
        const cp2x = lineWidth * 0.35;
        // Valor intermedio `cp2y` utilizado por la lógica de este bloque.
        const cp2y = valleyY;
        
        // Valor intermedio `cp3x` utilizado por la lógica de este bloque.
        const cp3x = lineWidth * 0.65;
        // Valor intermedio `cp3y` utilizado por la lógica de este bloque.
        const cp3y = valleyY;
        // Valor intermedio `cp4x` utilizado por la lógica de este bloque.
        const cp4x = lineWidth * 0.75;
        // Valor intermedio `cp4y` utilizado por la lógica de este bloque.
        const cp4y = baseY;
        
        return `M 0,${baseY} C ${cp1x},${cp1y} ${cp2x},${cp2y} ${midX},${valleyY} C ${cp3x},${cp3y} ${cp4x},${cp4y} ${lineWidth},${baseY}`;
      }
      
      case 'straight':
      default:
        return `M 0,${lineHeight / 2 + yOffset} L ${lineWidth},${lineHeight / 2 + yOffset}`;
    }
  };

  // ============================================
  // RENDERIZAR CAPAS DE TEXTO
  // Sombra → Contorno+Fill
  // ============================================
  const renderTextLayers = (pathId, textContent, dynFontSize, ls = letterSpacing, offsetX = 0, offsetY = 0) => {
    // Valor intermedio `strokeW` utilizado por la lógica de este bloque.
    const strokeW = dynFontSize * outlineWidth * 0.18;
    // Valor intermedio `shadX` utilizado por la lógica de este bloque.
    const shadX = shadowOffsetX * (dynFontSize / 12);
    // Valor intermedio `shadY` utilizado por la lógica de este bloque.
    const shadY = shadowOffsetY * (dynFontSize / 12);
    
    return (
      <g transform={offsetX || offsetY ? `translate(${offsetX}, ${offsetY})` : undefined}>
        {showShadow && (
          <text
            fill={color}
            stroke="none"
            fontSize={dynFontSize}
            fontFamily={fontFamily}
            fontWeight="bold"
            letterSpacing={ls}
            opacity={shadowOpacity}
            transform={`translate(${shadX}, ${shadY})`}
          >
            <textPath 
              href={`#${pathId}`} 
              textAnchor="middle"
              startOffset="50%"
            >
              {textContent}
            </textPath>
          </text>
        )}
        
        {showOutline && (
          <text
            fill={color}
            stroke={outlineColor}
            strokeWidth={strokeW}
            fontSize={dynFontSize}
            fontFamily={fontFamily}
            fontWeight="bold"
            letterSpacing={ls}
            paintOrder="stroke fill"
            strokeLinejoin="round"
            strokeLinecap="round"
          >
            <textPath 
              href={`#${pathId}`} 
              textAnchor="middle"
              startOffset="50%"
            >
              {textContent}
            </textPath>
          </text>
        )}
        
        {!showOutline && (
          <text
            fill={color}
            fontSize={dynFontSize}
            fontFamily={fontFamily}
            fontWeight="bold"
            letterSpacing={ls}
          >
            <textPath 
              href={`#${pathId}`} 
              textAnchor="middle"
              startOffset="50%"
            >
              {textContent}
            </textPath>
          </text>
        )}
      </g>
    );
  };

  if (!text) return null;

  // ============================================
  // RENDERIZADO: UNA LÍNEA
  // ============================================
  if (!isTwoLines) {
    // Valor intermedio `pathId` utilizado por la lógica de este bloque.
    const pathId = `curve-path-${uniqueId}`;
    // Valor intermedio `dynamicFontSize` utilizado por la lógica de este bloque.
    const dynamicFontSize = getDynamicFontSize(text, width, fontSize);
    
    return (
      <svg 
        width={width} 
        height={height} 
        viewBox={`0 0 ${width} ${height}`}
        className={className}
        style={{
          overflow: 'visible',
          display: 'block',
          ...style
        }}
      >
        <defs>
          <path 
            id={pathId} 
            d={getPath(width, height, curve, curveIntensity)} 
            fill="transparent"
          />
        </defs>
        
        {renderTextLayers(pathId, text, dynamicFontSize)}
      </svg>
    );
  }

  // ============================================
  // RENDERIZADO: DOS LÍNEAS
  // Ambas líneas usan curva "up" (montañita)
  // Segunda línea más pequeña y centrada
  // ============================================
  const line1Height = height * 0.6;
  // Valor intermedio `line2Height` utilizado por la lógica de este bloque.
  const line2Height = height * 0.55;
  // Valor intermedio `gap` utilizado por la lógica de este bloque.
  const gap = height * lineSpacing;
  // Valor intermedio `totalHeight` utilizado por la lógica de este bloque.
  const totalHeight = line1Height + gap + line2Height;
  
  // Valor intermedio `line2Width` utilizado por la lógica de este bloque.
  const line2Width = width * 0.72;
  // Valor intermedio `line2X` utilizado por la lógica de este bloque.
  const line2X = (width - line2Width) / 2;
  
  // Valor intermedio `line1FontSize` utilizado por la lógica de este bloque.
  const line1FontSize = getDynamicFontSize(textLines[0], width, fontSize, letterSpacing);
  // Valor intermedio `line2FontSize` utilizado por la lógica de este bloque.
  const line2FontSize = getDynamicFontSize(textLines[1], line2Width, fontSize * 0.82, letterSpacing * 0.9);
  
  // Valor intermedio `line2CurveIntensity` utilizado por la lógica de este bloque.
  const line2CurveIntensity = curveIntensity * 0.7;
  
  // Valor intermedio `pathId1` utilizado por la lógica de este bloque.
  const pathId1 = `curve-path-1-${uniqueId}`;
  // Valor intermedio `pathId2` utilizado por la lógica de este bloque.
  const pathId2 = `curve-path-2-${uniqueId}`;

  return (
    <svg 
      width={width} 
      height={totalHeight}
      viewBox={`0 0 ${width} ${totalHeight}`}
      className={className}
      style={{
        overflow: 'visible',
        display: 'block',
        ...style
      }}
    >
      <defs>
        {/* Línea 1: montañita completa */}
        <path 
          id={pathId1} 
          d={getPath(width, line1Height, 'up', curveIntensity, 0)} 
          fill="transparent"
        />
        {/* Línea 2: también montañita (up), más suave, ancho reducido */}
        <path 
          id={pathId2} 
          d={getPath(line2Width, line2Height, 'up', line2CurveIntensity, 0)} 
          fill="transparent"
        />
      </defs>
      
      {/* Línea 1: ancho completo */}
      {renderTextLayers(pathId1, textLines[0], line1FontSize)}
      
      {/* Línea 2: centrada, más angosta, misma forma de montañita */}
      {renderTextLayers(
        pathId2, 
        textLines[1], 
        line2FontSize, 
        letterSpacing * 0.9,
        line2X,
        line1Height + gap
      )}
    </svg>
  );
});

export default CurvedText;